import React, { useCallback } from 'react';
import { PropTypes } from 'prop-types';
// logic
import { useTranslation } from 'react-i18next';
import routes from 'utils/routes';
import useNewOrderButton from 'refactored/ui/nav/useNewOrderButton';
// style
import styled, { css } from 'styled-components';
import { media } from 'utils/style';
// components
import {
  AddIcon,
  ShipmentsIcon,
  AddressesIcon,
  BillingIcon,
  AnalyticsIcon,
} from 'refactored/ui/icons';
import { NavLink } from 'react-router-dom';
import MainMenuButton from 'refactored/ui/nav/mainMenu/MainMenuButton';
import MainMenuSeparator from 'refactored/ui/nav/mainMenu/MainMenuSeparator';
import { IS_TENANT, SERVICES, isTenantByName } from 'tenant/utils';
import { useSelector } from 'react-redux';
import { selectors as clientSelectors, constants } from 'state/clientConfig';
import { push } from 'connected-react-router';
import { useDispatch } from 'react-redux';

const MainMenuEntries = ({ className, isCollapsed }) => {
  const { t } = useTranslation(['mainMenu']);
  const dispatch = useDispatch();

  const isXpo = useSelector((state) =>
    clientSelectors.getSpecificClientConfig(state, constants.IS_XPO)
  );

  const { isNewOrderButtonDisabled, onNewOrderButtonClick } =
    useNewOrderButton();

  const onNewEmergencyButtonClick = () => {
    dispatch(push(`${routes.emergencies}/new`));
  };

  const isOrdersPageActive = useCallback(
    () => window.location.pathname.startsWith('/orders'),
    []
  );

  const isEmergenciesPageActive = useCallback(
    () => window.location.pathname === '/emergencies',
    []
  );

  const isAnalyticsPageActive = useCallback(
    () => window.location.pathname.startsWith('/dashboard'),
    []
  );

  return (
    <div className={className}>
      <MainMenuButton
        hidden={IS_TENANT || isTenantByName('xpo')}
        isCollapsed={isCollapsed}
        variant="NEW"
        text={t('new_order')}
        icon={<AddIcon />}
        onClick={onNewOrderButtonClick}
        disabled={isNewOrderButtonDisabled}
      />
      <MainMenuButton
        hidden={!isTenantByName('xpo')}
        isCollapsed={isCollapsed}
        variant="NEW"
        text={<>{t('new_emergency')}</>}
        icon={<AddIcon />}
        hideIconExpanded={true}
        onClick={onNewEmergencyButtonClick}
      />
      <MainMenuSeparator />
      {!isXpo && (
        <NavLink to={routes.orders.active} isActive={isOrdersPageActive}>
          <MainMenuButton
            tabIndex="-1"
            isCollapsed={isCollapsed}
            variant="DEFAULT"
            text={t('orders')}
            icon={<ShipmentsIcon />}
          />
        </NavLink>
      )}
      {isXpo && (
        <NavLink
          to={`${routes.emergencies}/`}
          isActive={isEmergenciesPageActive}
        >
          <MainMenuButton
            tabIndex="-1"
            isCollapsed={isCollapsed}
            variant="DEFAULT"
            text={t('orders')}
            icon={<ShipmentsIcon />}
          />
        </NavLink>
      )}
      {!IS_TENANT && (
        <>
          <MainMenuSeparator />
          <NavLink to={routes.addresses}>
            <MainMenuButton
              tabIndex="-1"
              isCollapsed={isCollapsed}
              variant="DEFAULT"
              text={t('addresses')}
              icon={<AddressesIcon />}
            />
          </NavLink>
        </>
      )}
      {!isXpo && (
        <>
          <MainMenuSeparator />
          <NavLink to={routes.billing}>
            <MainMenuButton
              tabIndex="-1"
              isCollapsed={isCollapsed}
              variant="DEFAULT"
              text={t('billing')}
              icon={<BillingIcon />}
            />
          </NavLink>
        </>
      )}
      <MainMenuSeparator />
      {SERVICES.SUPERSET && (
        <>
          <NavLink to={routes.dashboard} isActive={isAnalyticsPageActive}>
            <MainMenuButton
              tabIndex="-1"
              isCollapsed={isCollapsed}
              variant="DEFAULT"
              text={t('analytics')}
              icon={<AnalyticsIcon />}
            />
          </NavLink>
          <MainMenuSeparator />
        </>
      )}
    </div>
  );
};

MainMenuEntries.propTypes = {
  isCollapsed: PropTypes.bool,
  className: PropTypes.string,
};

const regularCSS = css`
  margin-top: 128px;
  margin-left: 60px;
  transition: margin-left 0.3s ease;
  a {
    text-decoration: none;
  }

  ${MainMenuSeparator}:first-of-type {
    margin-top: 48px;
  }
`;

const tabletCSS = css`
  ${(props) =>
    props.isCollapsed &&
    css`
      margin-left: 20px;
    `}
`;

export default styled(MainMenuEntries)`
  ${regularCSS};
  ${media.lessThan('huge')`${tabletCSS}`};
  .active button {
    border-right: 4px solid #6797fe;
    svg,
    svg path {
      fill: var(--white);
    }
    .buttonText {
      color: var(--white);
    }
  }
`;
